import classNames from 'classnames';
import dayjs from 'dayjs';
import { FC, ReactNode } from 'react';

export type TimelineStatus = 'success' | 'failure' | 'pending' | 'none';

interface TimelineProps {
  label: string;
  date: string | null | undefined;
  status?: TimelineStatus;
  customIndicator?: ReactNode;
  className?: string;
  isLast?: boolean;
  /**
   * Secondary content under the date — an actor, a rejection reason, anything that would
   * otherwise have to be concatenated onto `label` and read as a run-on sentence.
   */
  description?: ReactNode;
  /**
   * Hides the "Success"/"Failure" pill. Useful where every row shares a status and the pill is
   * pure noise; the coloured indicator still carries the meaning.
   */
  hideStatusLabel?: boolean;
  /** Appends a time-of-day to the date. Off by default so existing callers are unaffected. */
  showTime?: boolean;
}

const getDateFromString = (date: string | null | undefined, showTime: boolean) => {
  if (date === '–' || !date) return '–';
  return dayjs(date).format(showTime ? 'MMMM DD, YYYY, h:mm A' : 'MMMM DD, YYYY');
};

const VerticalTimeline: FC<TimelineProps> = ({
  label,
  date,
  status = 'none',
  customIndicator,
  className = '',
  isLast = false,
  description,
  hideStatusLabel = false,
  showTime = false,
}) => {
  const getStatusClass = () => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'failure':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return '';
    }
  };

  return (
    <div className={`flex gap-4 ${className}`}>
      <div className="flex flex-col items-center">
        <div className="flex-shrink-0">
          {customIndicator || (
            <div
              className={classNames('h-3 w-3 rounded-full', {
                'bg-green-600': status === 'success',
                'bg-red-600': status === 'failure',
                'bg-gray-200': status === 'pending',
              })}
            />
          )}
        </div>
        {!isLast && <div className="h-full w-0.5 bg-gray-200" />}
      </div>
      <div className="flex-1 pb-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-900">{label}</p>
          {status !== 'none' && !hideStatusLabel && (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusClass()}`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500">{getDateFromString(date, showTime)}</p>
        {description ? <div className="mt-1 text-sm text-gray-700">{description}</div> : null}
      </div>
    </div>
  );
};

export default VerticalTimeline;
